import * as React from 'react'
import { cn } from '@/lib/utils'
import { useUIVersion } from '@/utils/UIVersionContext'
import { Input as InputPrimitive } from '@base-ui/react/input'
import { Search } from 'lucide-react'

type InputSize = 'sm' | 'md' | 'lg'

type Props = Omit<React.ComponentProps<'input'>, 'size'> & {
  size?: InputSize
}

const sizeClasses: Record<InputSize, string> = {
  sm: 'h-7 text-sm',
  md: 'h-9 text-sm',
  lg: 'h-11 text-base',
}

export const Input = React.forwardRef<HTMLInputElement, Props>(
  ({ className, type, disabled, readOnly, size = 'sm', ...props }, ref) => {
    const isInteractive = !disabled && !readOnly
    const { isNewUI } = useUIVersion()

    return (
      <InputPrimitive
        ref={ref}
        type={type}
        disabled={disabled}
        readOnly={readOnly}
        data-slot='input'
        className={cn(
          'w-full min-w-0 rounded-md border border-input bg-transparent px-2.5 shadow-xs transition-[color,box-shadow,border-color] outline-none',
          sizeClasses[size],
          isInteractive && 'focus-visible:border-ring focus-visible:ring-ring/50',
          readOnly && 'cursor-not-allowed',
          disabled && 'cursor-not-allowed opacity-50 bg-gray-100',
          !isNewUI && 'px-1',
          className
        )}
        {...props}
      />
    )
  }
)

Input.displayName = 'Input'
type SearchInputProps = Omit<React.ComponentProps<typeof Input>, 'onSearch'> & {
  onSearch?: (value: string) => void
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ onSearch, ...props }, ref) => {
    return (
      <div className='relative'>
        <Search color='black' className='absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4' />

        <Input
          ref={ref}
          className='pl-8'
          {...props}
          onKeyDown={(e) => {
            props.onKeyDown?.(e)

            if (e.key === 'Enter') {
              onSearch?.(e.currentTarget.value)
            }
          }}
        />
      </div>
    )
  }
)

SearchInput.displayName = 'SearchInput'
